import { z } from "zod";

const strongPasswordSchema = z
  .string()
  .min(1, "Password is required")
  .regex(
    /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}/,
    "Password must have at least 1 uppercase, 1 lowercase, 1 number, 1 special character, and be at least 8 characters long."
  );

export const LoginFormFieldsName = {
  EMAIL: "email",
  PASSWORD: "password",
};

export const loginFormSchema = z.object({
  [LoginFormFieldsName.EMAIL]: z.string().min(1, "Email is required").email("Email is invalid"),
  [LoginFormFieldsName.PASSWORD]: z.string().min(1, "Password is required"),
});

export const loginFormDefaultValues = {
  [LoginFormFieldsName.EMAIL]: "",
  [LoginFormFieldsName.PASSWORD]: "",
};

export const SignUpFormFieldsName = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  EMAIL: "email",
  COUNTRY_CODE: "countryCode",
  PHONE: "phone",
  PASSWORD: "password",
};

export const signUpFormSchema = z.object({
  [SignUpFormFieldsName.FIRST_NAME]: z.string().min(1, "First Name is required"),
  [SignUpFormFieldsName.LAST_NAME]: z.string().min(1, "Last Name is required"),
  [SignUpFormFieldsName.EMAIL]: z.string().min(1, "Email is required").email("Email is invalid"),
  [SignUpFormFieldsName.COUNTRY_CODE]: z.string().min(1, "Country code is required"),
  [SignUpFormFieldsName.PHONE]: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^[0-9]+$/, "Phone number should contain only digits")
    .regex(/^[0-9]{10,11}$/, "Phone number should be between 10 to 11 digits"),
  [SignUpFormFieldsName.PASSWORD]: strongPasswordSchema,
});

export const signUpFormDefaultValues = {
  [SignUpFormFieldsName.FIRST_NAME]: "",
  [SignUpFormFieldsName.LAST_NAME]: "",
  [SignUpFormFieldsName.EMAIL]: "",
  [SignUpFormFieldsName.COUNTRY_CODE]: "+234",
  [SignUpFormFieldsName.PHONE]: "",
  [SignUpFormFieldsName.PASSWORD]: "",
};

export const ForgotPasswordFormFieldsName = {
  EMAIL: "email",
};

export const forgotPasswordFormSchema = z.object({
  [ForgotPasswordFormFieldsName.EMAIL]: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email"),
});

export const forgotPasswordFormDefaultValues = {
  [ForgotPasswordFormFieldsName.EMAIL]: "",
};

export const ResetPasswordFormFieldsName = {
  PASSWORD: "password",
  CONFIRM_PASSWORD: "confirmPassword",
};

export const resetPasswordFormSchema = z
  .object({
    [ResetPasswordFormFieldsName.PASSWORD]: z
      .string()
      .min(1, "Password is required")
      .min(8, "Password must be at least 8 characters long"),
    [ResetPasswordFormFieldsName.CONFIRM_PASSWORD]: z
      .string()
      .min(1, "Please confirm your password"),
  })
  .refine(
    (data) =>
      data[ResetPasswordFormFieldsName.PASSWORD] ===
      data[ResetPasswordFormFieldsName.CONFIRM_PASSWORD],
    {
      message: "Passwords do not match",
      path: [ResetPasswordFormFieldsName.CONFIRM_PASSWORD],
    }
  );

export const resetPasswordFormDefaultValues = {
  [ResetPasswordFormFieldsName.PASSWORD]: "",
  [ResetPasswordFormFieldsName.CONFIRM_PASSWORD]: "",
};

export const VendorPasswordResetFormFieldsName = {
  NEW_PASSWORD: "newPassword",
  CONFIRM_PASSWORD: "confirmPassword",
};

export const vendorPasswordResetFormSchema = z
  .object({
    [VendorPasswordResetFormFieldsName.NEW_PASSWORD]: strongPasswordSchema,
    [VendorPasswordResetFormFieldsName.CONFIRM_PASSWORD]: z
      .string()
      .min(1, "Please confirm your password"),
  })
  .refine(
    (data) =>
      data[VendorPasswordResetFormFieldsName.NEW_PASSWORD] ===
      data[VendorPasswordResetFormFieldsName.CONFIRM_PASSWORD],
    {
      message: "Passwords do not match!",
      path: [VendorPasswordResetFormFieldsName.CONFIRM_PASSWORD],
    }
  );

export const vendorPasswordResetFormDefaultValues = {
  [VendorPasswordResetFormFieldsName.NEW_PASSWORD]: "",
  [VendorPasswordResetFormFieldsName.CONFIRM_PASSWORD]: "",
};

export const OrganizationFormFieldsName = {
  NAME: "name",
  WEBSITE: "website",
  DESCRIPTION: "description",
  CONTACT_PHONE: "contact_phone",
  INSTAGRAM: "instagram",
  TIKTOK: "tiktok",
  TWITTER: "twitter",
  FACEBOOK: "facebook",
};

export const organizationFormSchema = z.object({
  [OrganizationFormFieldsName.NAME]: z.string().min(1, "Name is required"),
  [OrganizationFormFieldsName.WEBSITE]: z.string().min(1, "Website URL is required"),
  [OrganizationFormFieldsName.DESCRIPTION]: z.string().min(1, "Description is required"),
  [OrganizationFormFieldsName.CONTACT_PHONE]: z.string().optional(),
  [OrganizationFormFieldsName.INSTAGRAM]: z.string().optional(),
  [OrganizationFormFieldsName.TIKTOK]: z.string().optional(),
  [OrganizationFormFieldsName.TWITTER]: z.string().optional(),
  [OrganizationFormFieldsName.FACEBOOK]: z.string().optional(),
});

export const organizationFormDefaultValues = {
  [OrganizationFormFieldsName.NAME]: "",
  [OrganizationFormFieldsName.WEBSITE]: "",
  [OrganizationFormFieldsName.DESCRIPTION]: "",
  [OrganizationFormFieldsName.CONTACT_PHONE]: "",
  [OrganizationFormFieldsName.INSTAGRAM]: "",
  [OrganizationFormFieldsName.TIKTOK]: "",
  [OrganizationFormFieldsName.TWITTER]: "",
  [OrganizationFormFieldsName.FACEBOOK]: "",
};

export const ProfileSettingsFormFieldsName = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  USERNAME: "username",
};

export const profileSettingsFormSchema = z.object({
  [ProfileSettingsFormFieldsName.FIRST_NAME]: z.string().min(1, "First Name is required"),
  [ProfileSettingsFormFieldsName.LAST_NAME]: z.string().min(1, "Last Name is required"),
  [ProfileSettingsFormFieldsName.USERNAME]: z.string().optional(),
});

export const profileSettingsFormDefaultValues = {
  [ProfileSettingsFormFieldsName.FIRST_NAME]: "",
  [ProfileSettingsFormFieldsName.LAST_NAME]: "",
  [ProfileSettingsFormFieldsName.USERNAME]: "",
};

export const ChangePasswordFormFieldsName = {
  CURRENT_PASSWORD: "currentPassword",
  NEW_PASSWORD: "newPassword",
  CONFIRM_PASSWORD: "confirmPassword",
};

export const changePasswordFormSchema = z
  .object({
    [ChangePasswordFormFieldsName.CURRENT_PASSWORD]: z
      .string()
      .min(1, "Current password is required."),
    [ChangePasswordFormFieldsName.NEW_PASSWORD]: z
      .string()
      .min(1, "New password is required.")
      .min(8, "New password must be at least 8 characters long."),
    [ChangePasswordFormFieldsName.CONFIRM_PASSWORD]: z
      .string()
      .min(1, "Confirm password is required."),
  })
  .refine(
    (data) =>
      data[ChangePasswordFormFieldsName.NEW_PASSWORD] ===
      data[ChangePasswordFormFieldsName.CONFIRM_PASSWORD],
    {
      message: "New password and confirm password do not match!",
      path: [ChangePasswordFormFieldsName.CONFIRM_PASSWORD],
    }
  );

export const changePasswordFormDefaultValues = {
  [ChangePasswordFormFieldsName.CURRENT_PASSWORD]: "",
  [ChangePasswordFormFieldsName.NEW_PASSWORD]: "",
  [ChangePasswordFormFieldsName.CONFIRM_PASSWORD]: "",
};

export const OwnerEmailFormFieldsName = {
  EMAIL: "email",
};

export const ownerEmailFormSchema = z.object({
  [OwnerEmailFormFieldsName.EMAIL]: z
    .string()
    .min(1, "Owner email is required")
    .email("Enter a valid email address"),
});

export const ownerEmailFormDefaultValues = {
  [OwnerEmailFormFieldsName.EMAIL]: "",
};

export const OwnerOtpFormFieldsName = {
  OTP: "otp",
};

export const ownerOtpFormSchema = z.object({
  [OwnerOtpFormFieldsName.OTP]: z
    .string()
    .min(1, "Enter the verification code")
    .regex(/^\d{6}$/, "The code is 6 digits"),
});

export const ownerOtpFormDefaultValues = {
  [OwnerOtpFormFieldsName.OTP]: "",
};

// Multi-source knowledge model (Bot Knowledge Sources Contract). A source is
// added with exactly one reference matching its type: a URL (website), a PDF
// upload, or pasted manual text. plist_storefront/auto sources are seeded
// server-side and are not user-addable here.
export const KnowledgeSourceType = {
  WEBSITE: "website",
  PDF: "pdf",
  MANUAL: "manual",
};

// Server caps the extracted text of a single source at 60 KB; we reject
// obviously-over-cap manual text client-side so the user gets instant feedback.
export const MAX_KNOWLEDGE_SOURCE_BYTES = 60000;

export const KnowledgeSourceFormFieldsName = {
  SOURCE_TYPE: "sourceType",
  TITLE: "title",
  URL: "url",
  FILE: "file",
  TEXT: "text",
};

const isPdfFile = (file) =>
  Boolean(file) && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""));

export const knowledgeSourceFormSchema = z
  .object({
    [KnowledgeSourceFormFieldsName.SOURCE_TYPE]: z.enum([
      KnowledgeSourceType.WEBSITE,
      KnowledgeSourceType.PDF,
      KnowledgeSourceType.MANUAL,
    ]),
    [KnowledgeSourceFormFieldsName.TITLE]: z.string().optional(),
    [KnowledgeSourceFormFieldsName.URL]: z.string().optional(),
    [KnowledgeSourceFormFieldsName.FILE]: z.any().optional(),
    [KnowledgeSourceFormFieldsName.TEXT]: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const sourceType = data[KnowledgeSourceFormFieldsName.SOURCE_TYPE];

    if (sourceType === KnowledgeSourceType.WEBSITE) {
      const url = (data[KnowledgeSourceFormFieldsName.URL] || "").trim();
      if (!url) {
        ctx.addIssue({
          code: "custom",
          message: "Enter a URL to scrape.",
          path: [KnowledgeSourceFormFieldsName.URL],
        });
      } else {
        try {
          // Throws for anything that isn't a parseable absolute URL.
          // eslint-disable-next-line no-new
          new URL(url);
        } catch {
          ctx.addIssue({
            code: "custom",
            message: "Enter a valid URL (including https://).",
            path: [KnowledgeSourceFormFieldsName.URL],
          });
        }
      }
    }

    if (sourceType === KnowledgeSourceType.PDF) {
      const file = data[KnowledgeSourceFormFieldsName.FILE];
      if (!file) {
        ctx.addIssue({
          code: "custom",
          message: "Choose a PDF file to upload.",
          path: [KnowledgeSourceFormFieldsName.FILE],
        });
      } else if (!isPdfFile(file)) {
        ctx.addIssue({
          code: "custom",
          message: "Only PDF files are allowed.",
          path: [KnowledgeSourceFormFieldsName.FILE],
        });
      }
    }

    if (sourceType === KnowledgeSourceType.MANUAL) {
      const text = (data[KnowledgeSourceFormFieldsName.TEXT] || "").trim();
      if (!text) {
        ctx.addIssue({
          code: "custom",
          message: "Enter some text to add.",
          path: [KnowledgeSourceFormFieldsName.TEXT],
        });
      } else if (new TextEncoder().encode(text).length > MAX_KNOWLEDGE_SOURCE_BYTES) {
        ctx.addIssue({
          code: "custom",
          message: `Text is too long. Keep it under ${Math.round(
            MAX_KNOWLEDGE_SOURCE_BYTES / 1000
          )} KB.`,
          path: [KnowledgeSourceFormFieldsName.TEXT],
        });
      }
    }
  });

export const knowledgeSourceFormDefaultValues = {
  [KnowledgeSourceFormFieldsName.SOURCE_TYPE]: KnowledgeSourceType.WEBSITE,
  [KnowledgeSourceFormFieldsName.TITLE]: "",
  [KnowledgeSourceFormFieldsName.URL]: "",
  [KnowledgeSourceFormFieldsName.FILE]: null,
  [KnowledgeSourceFormFieldsName.TEXT]: "",
};

export const PromptGenerationFormFieldsName = {
  PROMPT_IDS: "promptIds",
  FLOW_IDS: "flowIds",
};

export const promptGenerationFormSchema = z.object({
  [PromptGenerationFormFieldsName.PROMPT_IDS]: z.array(z.string()),
  [PromptGenerationFormFieldsName.FLOW_IDS]: z.array(z.string()),
});

export const promptGenerationFormDefaultValues = {
  [PromptGenerationFormFieldsName.PROMPT_IDS]: [],
  [PromptGenerationFormFieldsName.FLOW_IDS]: [],
};
